import * as React from 'react';

import getConsumer from './consumer';
import { helpFlags } from './helpFlags';
import { HelpRule, HelpRuleOverrides, LiveViewClassroom, LiveViewDelta, LiveViewOnlineStudent, LiveViewPrompt, LiveViewRow } from './interfaces';
import { timeSpentAt } from './timeSpent';

import { requestGet } from '../../../../modules/request';

const SNAPSHOT_URL = '/teachers/progress_reports/live_view_data';
const RERENDER_INTERVAL_MS = 30000;
// Heartbeats and progress are pushed, so the refetch is only a safety net for
// anything missed; a reconnect also triggers one
const RECONCILE_INTERVAL_MS = 5 * 60 * 1000;
const FAILED_FETCH_RETRY_MS = 15000;
const PRESENCE_WINDOW_MS = 3 * 60 * 1000;
// How long a finished student stays on the report as Activity Completed
export const COMPLETED_ROW_MS = 60000;
// How long a removed row is kept in case its completion arrives late (a
// stopped beacon or snapshot can beat the completion job)
const DEPARTED_MS = 30000;
export const HELP_RULE_OVERRIDES_KEY = 'live-view-help-rule-overrides';

// Staff threshold edits live in this browser only; storage can be missing or
// blocked, which just means no edits
// TODO: May Need Help settings are stored in localStorage for now; move them to
// TeacherInfo so they persist across browsers and devices before launch
// The settings teachers can change; other stored fields are ignored
const OVERRIDE_FIELDS = ['minFailed', 'questionSeconds'];

// Keeps only non-negative numbers for known thresholds, so a hand-edited or
// out-of-date value cannot quietly break flagging in this browser
const validOverrides = (raw: unknown): HelpRuleOverrides => {
  if (!raw || typeof raw !== 'object') { return {}; }

  return Object.entries(raw).reduce((overrides, [toolKey, fields]) => {
    if (!fields || typeof fields !== 'object') { return overrides; }

    const valid = Object.fromEntries(Object.entries(fields).filter(([field, value]) => (
      OVERRIDE_FIELDS.includes(field) && typeof value === 'number' && Number.isFinite(value) && value >= 0
    )));
    return Object.keys(valid).length ? { ...overrides, [toolKey]: valid } : overrides;
  }, {});
};

const readOverrides = (): HelpRuleOverrides => {
  try {
    return validOverrides(JSON.parse(window.localStorage.getItem(HELP_RULE_OVERRIDES_KEY) || '{}'));
  } catch (e) {
    return {};
  }
};

const writeOverrides = (overrides: HelpRuleOverrides) => {
  try {
    if (Object.keys(overrides).length) {
      window.localStorage.setItem(HELP_RULE_OVERRIDES_KEY, JSON.stringify(overrides));
    } else {
      window.localStorage.removeItem(HELP_RULE_OVERRIDES_KEY);
    }
  } catch (e) {
    // Nothing to persist to; the edit still applies until the page reloads
  }
};

export const effectiveHelpRules = (rules: HelpRule[], overrides: HelpRuleOverrides): HelpRule[] => (
  rules.map(rule => (rule.unflaggedReason ? rule : { ...rule, ...(overrides[rule.toolKey] || {}) }))
);

// Carries what the previous version of a row was showing into the next one
const withTimeSpentFloor = (next: LiveViewRow, previous: LiveViewRow|undefined, at: string|null): LiveViewRow => {
  if (!previous) { return next; }
  if (!at) { return { ...next, timeSpentFloor: previous.timeSpentFloor }; }
  return { ...next, timeSpentFloor: timeSpentAt(previous, new Date(at)) };
};

const position = (prompt: LiveViewPrompt) => prompt.index ?? Number.POSITIVE_INFINITY;

// Adds an answer once, ordered like LiveView::RowSerializer#prompts
export const withAnswer = (row: LiveViewRow, prompt: LiveViewPrompt): LiveViewRow => {
  if (row.prompts.some(existing => existing.id === prompt.id)) { return row; }

  const prompts = [...row.prompts, prompt].map((answer, arrival) => ({ answer, arrival }))
    .sort((a, b) => (position(a.answer) - position(b.answer)) || (a.arrival - b.arrival))
    .map(({ answer }) => answer);
  return { ...row, prompts };
};

export const applyDelta = (rowsByUid: Record<string, LiveViewRow>, delta: LiveViewDelta): Record<string, LiveViewRow> => {
  const updated = { ...rowsByUid };
  if (delta.type === 'student_answered') {
    const existing = rowsByUid[delta.activitySessionUid];
    if (existing) { updated[delta.activitySessionUid] = withAnswer(existing, delta.prompt); }
    return updated;
  }
  if (delta.type === 'student_updated') {
    const existing = rowsByUid[delta.row.activitySessionUid];
    // Non-premium deltas are redacted server-side; keep the showcase row's
    // full data from the last snapshot rather than blanking it
    updated[delta.row.activitySessionUid] = delta.row.redacted && existing && !existing.redacted
      ? { ...existing, lastSeenAt: delta.row.lastSeenAt, startedAt: delta.row.startedAt }
      : withTimeSpentFloor(delta.row, existing, delta.row.timeSpentAsOf);
  } else if (delta.type === 'student_removed') {
    delete updated[delta.activitySessionUid];
  } else if (delta.type === 'student_seen') {
    // Heartbeat: refresh presence, time spent and (premium stream only) the
    // time on the current question, on a row we already know
    const existing = rowsByUid[delta.activitySessionUid];
    if (existing) {
      updated[delta.activitySessionUid] = withTimeSpentFloor({
        ...existing,
        lastSeenAt: delta.lastSeenAt,
        timeSpentAsOf: delta.lastSeenAt,
        timeSpentSeconds: delta.timeSpentSeconds === null ? existing.timeSpentSeconds : delta.timeSpentSeconds,
        // The basic stream carries no question time, so the (showcase) row
        // stops counting it on rather than extrapolating a stale value forever
        ...(delta.questionTimeSeconds === undefined
          ? { questionTimeAsOf: null }
          : { questionTimeSeconds: delta.questionTimeSeconds, questionTimeAsOf: delta.lastSeenAt }),
        stage: delta.stage === undefined ? existing.stage : delta.stage,
        idle: delta.idle === undefined ? existing.idle : delta.idle,
        progressPending: delta.progressPending === undefined ? existing.progressPending : delta.progressPending,
      }, existing, delta.lastSeenAt);
    }
  }
  return updated;
};

const stillOnline = (row: { lastSeenAt: string|null }, now: Date): boolean => {
  if (!row.lastSeenAt) { return false; }
  return now.getTime() - new Date(row.lastSeenAt).getTime() <= PRESENCE_WINDOW_MS;
};

// A class the student stops heartbeating in (they left it) ages out like presence
const seenInClassrooms = (student: LiveViewOnlineStudent): Record<number, string> => (
  student.seenByClassroom || Object.fromEntries((student.classroomIds || [student.classroomId]).map(id => [id, student.lastSeenAt]))
);

// Site-wide presence, keyed by student rather than activity session
export const applyOnlineDelta = (onlineByUserId: Record<number, LiveViewOnlineStudent>, delta: LiveViewDelta): Record<number, LiveViewOnlineStudent> => {
  if (delta.type !== 'student_online' && delta.type !== 'student_offline') { return onlineByUserId; }
  const updated = { ...onlineByUserId };
  const existing = onlineByUserId[delta.userId];
  // One delta per class stream, so a student in several of the teacher's classes sends several
  const seenByClassroom = existing ? { ...seenInClassrooms(existing) } : {};
  if (delta.type === 'student_online') {
    seenByClassroom[delta.classroomId] = delta.lastSeenAt;
    updated[delta.userId] = { userId: delta.userId, studentName: delta.studentName, classroomId: delta.classroomId, seenByClassroom, lastSeenAt: delta.lastSeenAt };
    return updated;
  }

  delete seenByClassroom[delta.classroomId];
  if (Object.keys(seenByClassroom).length) {
    updated[delta.userId] = { ...existing, seenByClassroom };
  } else {
    delete updated[delta.userId];
  }
  return updated;
};

// A student who is online but has no activity open still gets a (sparse) row
const onlineOnlyRow = (student: LiveViewOnlineStudent, now: Date): LiveViewRow => ({
  activitySessionUid: `online-${student.userId}`,
  userId: student.userId,
  studentName: student.studentName,
  classroomId: student.classroomId,
  classroomIds: Object.entries(seenInClassrooms(student)).filter(([, lastSeenAt]) => stillOnline({ lastSeenAt }, now)).map(([id]) => Number(id)),
  activityName: null,
  toolKey: null,
  startedAt: student.lastSeenAt,
  lastSeenAt: student.lastSeenAt,
  timeSpentSeconds: null,
  timeSpentAsOf: null,
  totalPrompts: null,
  completedPromptCount: 0,
  optimalPromptCount: 0,
  prompts: [],
  stage: 'online',
  onlineOnly: true,
});

// For a non-premium teacher the server sends one row in full (the "showcase");
// when that row is replaced by a redacted delta or removed, or a redacted row
// arrives while none is shown in full (the first activity on an empty report),
// only a fresh snapshot can say which row is shown in full now
export const showcaseChanged = (rowsByUid: Record<string, LiveViewRow>, delta: LiveViewDelta): boolean => {
  if (delta.type === 'student_updated' && delta.row.redacted && !Object.values(rowsByUid).some(row => !row.redacted)) { return true; }
  // The basic stream lacks stage and pending status, so the showcase row refetches
  if (delta.type === 'student_seen') {
    const seen = rowsByUid[delta.activitySessionUid];
    if (!seen || seen.redacted) { return false; }
    return !!delta.stageChanged || (!!seen.progressPending && delta.progressPending === undefined);
  }

  const uid = delta.type === 'student_seen' ? null : delta.activitySessionUid || delta.row?.activitySessionUid;
  const existing = uid && rowsByUid[uid];
  if (!existing || existing.redacted) { return false; }
  if (delta.type === 'student_updated') { return !!delta.row.redacted; }
  // Premium teachers see every row in full, so removing one reveals nothing new
  return delta.type === 'student_removed' && Object.values(rowsByUid).some(row => row.redacted);
};

interface LiveViewState {
  rowsByUid: Record<string, LiveViewRow>,
  onlineByUserId: Record<number, LiveViewOnlineStudent>,
  loading: boolean,
  clockOffsetMs: number,
  tick: number,
  // Sessions completed since the page loaded, so views can refresh what
  // depends on completions (the activity feed)
  completedCount: number,
  // The server's May Need Help rules, and this browser's edits to them
  helpRules: HelpRule[],
  helpRuleOverrides: HelpRuleOverrides,
  // Kept apart from rowsByUid so a snapshot refetch doesn't drop them early
  completedByUid: Record<string, LiveViewRow>,
  classrooms: LiveViewClassroom[],
}

// One store per page, shared by every component that shows live view data
// (the navbar count, the dashboard widget and the report are separate
// ReactOnRails roots, so React context cannot span them): a single snapshot
// fetch, a single LiveView::TeacherChannel subscription, one rerender tick and one
// safety refetch, started with the first subscriber and stopped with the last.
const createStore = () => {
  const initialState = (): LiveViewState => ({
    rowsByUid: {}, onlineByUserId: {}, loading: true, clockOffsetMs: 0, tick: 0, completedCount: 0, helpRules: [], helpRuleOverrides: readOverrides(), completedByUid: {}, classrooms: [],
  });
  let state: LiveViewState = initialState();
  const listeners = new Set<() => void>();
  // Consumers showing rows (report, widget) as opposed to just the count
  let detailConsumers = 0;
  // Deltas received while a snapshot is in flight are newer than it, so they
  // are replayed on top of the snapshot instead of being overwritten by it
  let inFlightDeltas: LiveViewDelta[]|null = null;
  // A fetch asked for while one is in flight may need what that one cannot
  // give (the watch flag, deltas broadcast before the socket subscribed), so
  // it runs once the in-flight one lands
  let refetchQueued = false;
  // Students heard heartbeating without a row here, refetched for once each
  // until their row shows up again
  const refetchedForUids = new Set<string>();
  // Bumped by stop(), so a snapshot requested before it is ignored when it lands
  let generation = 0;
  let subscription = null;
  let rerenderInterval = null;
  let reconcileInterval = null;
  let retryTimeout = null;
  const completedTimeouts = new Map<string, number>();
  const departed = new Map<string, { row: LiveViewRow, at: number }>();

  const noteDeparted = (rows: LiveViewRow[]) => {
    const at = Date.now();
    departed.forEach((entry, uid) => { if (at - entry.at > DEPARTED_MS) { departed.delete(uid); } });
    rows.forEach(row => departed.set(row.activitySessionUid, { row, at }));
  };

  const answerDeparted = (uid: string, prompt: LiveViewPrompt) => {
    const entry = departed.get(uid);
    if (entry) { departed.set(uid, { ...entry, row: withAnswer(entry.row, prompt) }); }
  };

  const answerCompleted = (delta: LiveViewDelta): Record<string, LiveViewRow> => {
    if (delta.type !== 'student_answered' || !state.completedByUid[delta.activitySessionUid]) { return state.completedByUid; }

    return { ...state.completedByUid, [delta.activitySessionUid]: withAnswer(state.completedByUid[delta.activitySessionUid], delta.prompt) };
  };

  const recentlyDeparted = (uid: string): LiveViewRow|undefined => {
    const entry = departed.get(uid);
    return entry && Date.now() - entry.at <= DEPARTED_MS ? entry.row : undefined;
  };

  const setState = (patch: Partial<LiveViewState>) => {
    state = { ...state, ...patch };
    refetchedForUids.forEach((uid) => { if (state.rowsByUid[uid]) { refetchedForUids.delete(uid); } });
    listeners.forEach(listener => listener());
  };

  const fetchSnapshot = () => {
    if (inFlightDeltas) {
      refetchQueued = true;
      return;
    }
    refetchQueued = false;
    inFlightDeltas = [];
    const requestGeneration = generation;
    const failed = () => {
      if (requestGeneration !== generation) { return; }
      inFlightDeltas = null;
      refetchQueued = false;
      // Keep showing whatever we have (or the empty state) rather than "Loading…"
      setState({ loading: false });
      window.clearTimeout(retryTimeout);
      retryTimeout = window.setTimeout(fetchSnapshot, FAILED_FETCH_RETRY_MS);
    };
    requestGet(detailConsumers > 0 ? `${SNAPSHOT_URL}?watch=1` : SNAPSHOT_URL, (body) => {
      if (requestGeneration !== generation) { return; }
      const nextRows = {};
      (body.rows || []).forEach((row: LiveViewRow) => {
        nextRows[row.activitySessionUid] = withTimeSpentFloor(row, state.rowsByUid[row.activitySessionUid], body.serverNow || null);
      });
      const nextOnline = {};
      (body.online || []).forEach((student: LiveViewOnlineStudent) => { nextOnline[student.userId] = student; });
      noteDeparted(Object.values(state.rowsByUid).filter(row => !nextRows[row.activitySessionUid]));
      const pendingDeltas = inFlightDeltas || [];
      inFlightDeltas = null;
      const rowsByUid = pendingDeltas.reduce(applyDelta, nextRows);
      const clockOffsetMs = body.serverNow ? new Date(body.serverNow).getTime() - Date.now() : state.clockOffsetMs;
      setState({
        rowsByUid,
        completedByUid: withoutRestarted(state.completedByUid, rowsByUid, clockOffsetMs),
        onlineByUserId: pendingDeltas.reduce(applyOnlineDelta, nextOnline),
        loading: false,
        clockOffsetMs,
        helpRules: body.helpRules || state.helpRules,
        classrooms: body.classrooms || state.classrooms,
      });
      if (refetchQueued) { fetchSnapshot(); }
    }, failed).catch(failed);
  };

  // A heartbeat for a session with a row on the server but none here means its
  // student_updated was missed; without this it would wait for the reconcile.
  // A snapshot in flight will bring the row anyway.
  const missedRow = (delta: LiveViewDelta): boolean => {
    if (inFlightDeltas || delta.type !== 'student_seen') { return false; }
    if (state.rowsByUid[delta.activitySessionUid] || refetchedForUids.has(delta.activitySessionUid)) { return false; }

    refetchedForUids.add(delta.activitySessionUid);
    return true;
  };

  const dropCompleted = (uid: string) => {
    completedTimeouts.delete(uid);
    const completedByUid = { ...state.completedByUid };
    delete completedByUid[uid];
    setState({ completedByUid });
  };

  // A student who has started another activity is no longer shown as finished,
  // even once that activity's row goes (a stopped beacon, a reload). One in
  // another class still shows them finished when filtered to this class.
  const withoutRestarted = (
    completedByUid: Record<string, LiveViewRow>, rowsByUid: Record<string, LiveViewRow>, clockOffsetMs = state.clockOffsetMs
  ): Record<string, LiveViewRow> => {
    if (!Object.keys(completedByUid).length) { return completedByUid; }

    const now = new Date(Date.now() + clockOffsetMs);
    const current = Object.values(rowsByUid).filter(row => stillOnline(row, now));
    let changed = false;
    const kept: Record<string, LiveViewRow> = {};
    Object.entries(completedByUid).forEach(([uid, completed]) => {
      const others = current.filter(row => row.userId === completed.userId && row.activitySessionUid !== uid);
      if (others.some(row => row.classroomId === completed.classroomId)) {
        window.clearTimeout(completedTimeouts.get(uid));
        completedTimeouts.delete(uid);
        changed = true;
      } else if (others.length && !completed.startedElsewhere) {
        kept[uid] = { ...completed, startedElsewhere: true };
        changed = true;
      } else {
        kept[uid] = completed;
      }
    });
    return changed ? kept : completedByUid;
  };

  // Freezes the finished student's time for COMPLETED_ROW_MS
  const holdCompleted = (uid: string): Record<string, LiveViewRow> => {
    const row = state.rowsByUid[uid] || recentlyDeparted(uid);
    if (!row) { return state.completedByUid; }

    const now = new Date(Date.now() + state.clockOffsetMs);
    window.clearTimeout(completedTimeouts.get(uid));
    completedTimeouts.set(uid, window.setTimeout(() => dropCompleted(uid), COMPLETED_ROW_MS));
    return {
      ...state.completedByUid,
      [uid]: { ...row, completed: true, timeSpentSeconds: timeSpentAt(row, now), timeSpentAsOf: null, questionTimeAsOf: null, progressPending: false },
    };
  };

  const receiveDelta = (delta: LiveViewDelta) => {
    if (inFlightDeltas) { inFlightDeltas.push(delta); }
    const refetch = showcaseChanged(state.rowsByUid, delta) || missedRow(delta);
    const completedUid = delta.type === 'student_removed' && delta.completed ? delta.activitySessionUid : null;
    if (delta.type === 'student_removed' && !completedUid && state.rowsByUid[delta.activitySessionUid]) { noteDeparted([state.rowsByUid[delta.activitySessionUid]]); }
    if (delta.type === 'student_answered') { answerDeparted(delta.activitySessionUid, delta.prompt); }
    const rowsByUid = applyDelta(state.rowsByUid, delta);
    setState({
      completedByUid: withoutRestarted(completedUid ? holdCompleted(completedUid) : answerCompleted(delta), rowsByUid),
      rowsByUid,
      onlineByUserId: applyOnlineDelta(state.onlineByUserId, delta),
      completedCount: state.completedCount + (completedUid ? 1 : 0),
    });
    if (refetch) { fetchSnapshot(); }
  };

  const start = () => {
    // Pick up edits saved since the store was last running (another page or tab)
    state = { ...state, helpRuleOverrides: readOverrides() };
    fetchSnapshot();

    let connectedBefore = false;
    subscription = getConsumer().subscriptions.create(
      { channel: 'LiveView::TeacherChannel', },
      {
        received: receiveDelta,
        connected: () => {
          // A watch performed before the socket opened was dropped
          if (detailConsumers > 0) { subscription?.perform('watch'); }
          // Deltas broadcast before this confirmation were missed: after a
          // drop, or, for pages showing rows, between the initial snapshot's
          // read and the subscription
          if (connectedBefore || detailConsumers > 0) { fetchSnapshot(); }
          connectedBefore = true;
        },
      }
    );
    rerenderInterval = window.setInterval(() => setState({ tick: state.tick + 1 }), RERENDER_INTERVAL_MS);
    reconcileInterval = window.setInterval(fetchSnapshot, RECONCILE_INTERVAL_MS);
  };

  const stop = () => {
    subscription?.unsubscribe();
    subscription = null;
    window.clearInterval(rerenderInterval);
    window.clearInterval(reconcileInterval);
    window.clearTimeout(retryTimeout);
    completedTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    completedTimeouts.clear();
    departed.clear();
    inFlightDeltas = null;
    refetchQueued = false;
    refetchedForUids.clear();
    generation += 1;
    detailConsumers = 0;
    state = initialState();
  };

  // A non-premium teacher's students only report prompts once a teacher is
  // looking at rows; tell the channel. Before the socket opens this is dropped,
  // and connecting performs it and refetches instead.
  const watch = () => {
    subscription?.perform('watch');
  };

  const setHelpRuleOverride = (toolKey: string, override: HelpRuleOverrides[string]) => {
    const helpRuleOverrides = { ...state.helpRuleOverrides, [toolKey]: { ...(state.helpRuleOverrides[toolKey] || {}), ...override } };
    writeOverrides(helpRuleOverrides);
    setState({ helpRuleOverrides });
  };

  return {
    getState: () => state,
    setHelpRuleOverride,
    subscribe: (listener: () => void, { details = false } = {}) => {
      if (details) { detailConsumers += 1; }
      if (listeners.size === 0) {
        start();
      } else if (details && detailConsumers === 1) {
        watch();
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (details) { detailConsumers -= 1; }
        if (listeners.size === 0) { stop(); }
      };
    },
  };
};

let store = null;
const getStore = () => {
  if (!store) { store = createStore(); }
  return store;
};

interface LiveViewRowsResult {
  rows: LiveViewRow[],
  completedRows: LiveViewRow[],
  students: LiveViewRow[],
  studentCount: number,
  loading: boolean,
  now: Date,
  completedCount: number,
  helpRules: HelpRule[],
  // The server's rules, before this browser's edits: what the settings show as defaults
  defaultHelpRules: HelpRule[],
  setHelpRuleOverride: (toolKey: string, override: HelpRuleOverrides[string]) => void,
  classrooms: LiveViewClassroom[],
  // The class the rows are filtered to: the one asked for, if the teacher teaches it
  classroomId: number|null,
}

// An online-only student can be in several of the teacher's classes
export const inClassroom = (row: Pick<LiveViewRow, 'classroomId'|'classroomIds'|'onlineOnly'>, classroomId: number|null): boolean => {
  if (classroomId === null) { return true; }

  return row.onlineOnly ? (row.classroomIds || []).includes(classroomId) : row.classroomId === classroomId;
};

// `rows`: students in an activity, with May Need Help flags.
// `completedRows`: students who just finished, never flagged.
// `students`: both, plus online students with no activity; what the table shows.
// `studentCount`: distinct students.
export const useLiveViewRows = ({ details = false, classroomId = null }: { details?: boolean, classroomId?: number|null } = {}): LiveViewRowsResult => {
  const liveViewStore = getStore();
  const subscribe = React.useCallback((listener: () => void) => liveViewStore.subscribe(listener, { details }), [liveViewStore, details]);
  // The installed React types return unknown here
  const state = React.useSyncExternalStore(subscribe, liveViewStore.getState) as LiveViewState;

  const now = new Date(Date.now() + state.clockOffsetMs);
  const helpRules = React.useMemo(() => effectiveHelpRules(state.helpRules, state.helpRuleOverrides), [state.helpRules, state.helpRuleOverrides]);
  const rulesByTool = React.useMemo(() => new Map(helpRules.map(rule => [rule.toolKey, rule])), [helpRules]);
  const selectedClassroomId = state.classrooms.some(classroom => classroom.id === classroomId) ? classroomId : null;
  // Filter first, so a student's row in another class can't hide them here
  const inSelectedClassroom = (row: LiveViewRow) => inClassroom(row, selectedClassroomId);
  // Flags and the never-flagged reason both come from the rule in effect
  const rows = Object.values(state.rowsByUid)
    .filter(row => stillOnline(row, now) && inSelectedClassroom(row))
    .map((row) => {
      const rule = rulesByTool.get(row.toolKey);
      return { ...row, ...helpFlags(row, rule, now), unflaggedReason: rule?.unflaggedReason || null, helpRule: rule || null };
    });
  const inActivity = new Set(rows.map(row => row.userId));
  // A student who started another activity shows that one instead
  const completedRows = Object.values(state.completedByUid)
    .filter(row => inSelectedClassroom(row) && !inActivity.has(row.userId) && !(row.startedElsewhere && selectedClassroomId === null))
    .map(row => ({ ...row, lowScore: false, tooLong: false, unflaggedReason: null, helpRule: null }));
  const listed = new Set([...inActivity, ...completedRows.map(row => row.userId)]);
  const onlineOnly = Object.values(state.onlineByUserId)
    .filter(student => stillOnline(student, now) && !listed.has(student.userId))
    .map(student => onlineOnlyRow(student, now))
    .filter(inSelectedClassroom);
  const students = [...rows, ...completedRows, ...onlineOnly];
  const studentCount = new Set(students.map(student => student.userId)).size;
  return {
    rows,
    completedRows,
    students,
    studentCount,
    loading: state.loading,
    now,
    completedCount: state.completedCount,
    helpRules,
    defaultHelpRules: state.helpRules,
    setHelpRuleOverride: liveViewStore.setHelpRuleOverride,
    classrooms: state.classrooms,
    classroomId: selectedClassroomId,
  };
};
